import React, { useState, useCallback } from 'react';
import InputField from './InputField';
import LinkedFieldContainer from './LinkedFieldContainer';
import { calculateFormProgress } from '../utils/formUtiljs';
import { getApiUrl } from '../Api/apiUrl';


const DynamicFormRenderer = ({
  config,
  register,
  errors,
  setValue,
  unregister,
  control,
  watch,
  onSubmit,
  className = ""
}) => {
  const {
    metadata,
    sections,
    progress,
    submission,
    styling,
  } = config;

  // File upload state management
  const [fileUploads, setFileUploads] = useState({});
  const [uploadingFiles, setUploadingFiles] = useState(new Set());
  const [uploadErrors, setUploadErrors] = useState({});

  // File upload service function
  const uploadFile = useCallback(async (file, fieldName) => {
    if (!file || !(file instanceof File)) {
      throw new Error('Invalid file provided');
    }

    // Preserve the original filename
    const originalFileName = file.name;

    // Add to uploading set
    setUploadingFiles(prev => new Set([...prev, fieldName]));

    try {
      // Convert file to base64
      const reader = new FileReader();
      const base64Data = await new Promise((resolve, reject) => {
        reader.onload = () => {
          // Extract base64 from data URL (remove "data:type;base64," prefix)
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Send as JSON with base64
      const response = await fetch(getApiUrl('/files'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          environment: metadata.environment,
          files: [
            {
              name: originalFileName,
              mimeType: file.type,
              data: base64Data
            }
          ]
        })
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const uploadResult = await response.json();

      // Store upload result - extract the id from the response
      const uploadedFileId = uploadResult[0]?.id || uploadResult[0];

      // Store file info with both ID and original filename
      const fileInfo = {
        id: uploadedFileId,
        name: originalFileName
      };

      setFileUploads(prev => ({
        ...prev,
        [fieldName]: fileInfo
      }));

      // Update form field with file info object containing both ID and name
      setValue(fieldName, fileInfo, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true
      });

      // Clear any previous upload errors for this field
      setUploadErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });

      return uploadResult[0];

    } catch (error) {
      console.error(`File upload error for ${fieldName}:`, error);

      // Store error
      setUploadErrors(prev => ({
        ...prev,
        [fieldName]: error.message
      }));

      throw error;
    } finally {
      // Remove from uploading set
      setUploadingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(fieldName);
        return newSet;
      });
    }
  }, [setValue, metadata.environment]);

  // File delete service function
  const deleteFile = useCallback(async (fileId, fieldName) => {
    try {
      console.log(`[DynamicFormRenderer] Iniciando eliminación de archivo:`, { fileId, fieldName });

      const url = getApiUrl(`/files/${fileId}`);
      console.log(`[DynamicFormRenderer] URL de eliminación:`, url);

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log(`[DynamicFormRenderer] Respuesta del servidor:`, { status: response.status, ok: response.ok });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Delete failed: ${response.statusText} - ${JSON.stringify(errorData)}`);
      }

      const responseData = await response.json();
      console.log(`[DynamicFormRenderer] Archivo eliminado exitosamente:`, responseData);

      // Remove from file uploads
      setFileUploads(prev => {
        const newUploads = { ...prev };
        delete newUploads[fieldName];
        return newUploads;
      });

      // Clear any previous upload errors for this field
      setUploadErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });

      return true;
    } catch (error) {
      console.error(`[DynamicFormRenderer] Error al eliminar archivo para ${fieldName}:`, error);

      // Store error
      setUploadErrors(prev => ({
        ...prev,
        [fieldName]: `Failed to delete file: ${error.message}`
      }));

      throw error;
    }
  }, []);

  // Check if any files are currently uploading
  const isAnyFileUploading = uploadingFiles.size > 0;


  const getCurrentProgress = () => {
    if (!progress?.enabled) {
      return 0;
    }

    const watchedValues = watch();
    const formState = { errors, watchedValues };

    // If tracking required fields specifically
    if (progress?.trackRequiredFields) {
      // Get actual field names accounting for linked containers
      const actualRequiredFields = [];
      for (const section of config.sections) {
        if (section.fields) {
          for (const field of section.fields) {
            if (field.required) {
              actualRequiredFields.push(getActualFieldName(field, section));
            }
          }
        }
      }
      return calculateFormProgress(watchedValues, actualRequiredFields);
    }

    // Enhanced progress tracking considering all validations
    return calculateEnhancedProgress(formState, config);
  };


  const getActualFieldName = (field, section) => {

    if (section.linkedContainer) {
      return `${section.linkedContainer.displayId}_${field?.name}`;
    }
    return field?.name ?? "";
  };


  const calculateEnhancedProgress = (formState, config) => {
    const { errors, watchedValues } = formState;
    let totalFields = 0;
    let validFields = 0;

    config.sections.forEach(section => {
      if (section.fields) {
        section.fields.forEach(field => {

          if (section.linkedContainer) {
            return;
          }

          totalFields++;

          const fieldValue = watchedValues[field.name];
          const fieldError = errors[field.name];
          // Handle file fields differently - check if file object exists
          const isFileField = field.type === 'file' || field.type === 'signature';
          const hasValue = isFileField
            ? fieldValue && (fieldValue instanceof File || (typeof fieldValue === 'object' && fieldValue.name))
            : fieldValue && fieldValue.toString().trim() !== '';
          const hasNoErrors = !fieldError;

          if (field.required) {
            if (hasValue && hasNoErrors) {
              validFields++;
            }
          } else {

            if (hasNoErrors) {
              validFields++;
            }
          }
        });
      }
    });


    const linkedContainerProgress = calculateLinkedContainerProgress(formState, config);

    if (totalFields > 0) {
      const baseProgress = Math.round((validFields / totalFields) * 100);
      return Math.min(100, baseProgress + linkedContainerProgress);
    }

    return linkedContainerProgress;
  };

  const calculateLinkedContainerProgress = (formState, config) => {
    const { errors, watchedValues } = formState;
    let linkedContainerFields = 0;
    let validLinkedFields = 0;

    config.sections.forEach(section => {
      if (section.linkedContainer && section.fields) {
        section.fields.forEach(field => {
          linkedContainerFields++;

          const actualFieldName = getActualFieldName(field, section);

          const fieldValue = watchedValues[actualFieldName];
          const fieldError = errors[actualFieldName];
          // Handle file fields differently - check if file object exists
          const isFileField = field.type === 'file' || field.type === 'signature';
          const hasValue = isFileField
            ? fieldValue && (fieldValue instanceof File || (typeof fieldValue === 'object' && fieldValue.name))
            : fieldValue && fieldValue.toString().trim() !== '';
          const hasNoErrors = !fieldError;

          if (field.required) {
            if (hasValue && hasNoErrors) {
              validLinkedFields++;
            }
          } else {

            if (hasNoErrors) {
              validLinkedFields++;
            }
          }
        });
      }
    });

    if (linkedContainerFields === 0) return 0;

    const maxLinkedProgress = 50;
    const linkedProgress = Math.round((validLinkedFields / linkedContainerFields) * 100);
    return Math.round((linkedProgress / 100) * maxLinkedProgress);
  };

  const currentProgress = getCurrentProgress();

  // Convert numeric column spans to Tailwind classes (static to avoid dynamic class issues)
  const getColumnClasses = (columnSpan) => {
    const spanMap = {
      1: 'col-span-1',
      2: 'col-span-2',
      3: 'col-span-3',
      4: 'col-span-4',
      6: 'col-span-6',
      12: 'col-span-12'
    };

    const mdSpanMap = {
      1: 'md:col-span-1',
      2: 'md:col-span-2',
      3: 'md:col-span-3',
      4: 'md:col-span-4',
      6: 'md:col-span-6',
      12: 'md:col-span-12'
    };

    const lgSpanMap = {
      1: 'lg:col-span-1',
      2: 'lg:col-span-2',
      3: 'lg:col-span-3',
      4: 'lg:col-span-4',
      6: 'lg:col-span-6',
      12: 'lg:col-span-12'
    };

    if (typeof columnSpan === 'object') {
      const classes = [];
      if (columnSpan.default) classes.push(spanMap[columnSpan.default] || 'col-span-1');
      if (columnSpan.md) classes.push(mdSpanMap[columnSpan.md] || '');
      if (columnSpan.lg) classes.push(lgSpanMap[columnSpan.lg] || '');
      return classes.filter(Boolean).join(' ');
    }
    return spanMap[columnSpan] || 'col-span-1';
  };

  // Generate grid layout classes based on column configuration
  const getGridClasses = (columns, gap = 'gap-6') => {
    if (!columns) return 'grid-cols-1 gap-6';

    const defaultColsMap = {
      1: 'grid-cols-1',
      2: 'grid-cols-2',
      3: 'grid-cols-3',
      4: 'grid-cols-4',
      6: 'grid-cols-6',
      12: 'grid-cols-12'
    };

    const mdColsMap = {
      1: 'md:grid-cols-1',
      2: 'md:grid-cols-2',
      3: 'md:grid-cols-3',
      4: 'md:grid-cols-4',
      6: 'md:grid-cols-6',
      12: 'md:grid-cols-12'
    };

    const lgColsMap = {
      1: 'lg:grid-cols-1',
      2: 'lg:grid-cols-2',
      3: 'lg:grid-cols-3',
      4: 'lg:grid-cols-4',
      6: 'lg:grid-cols-6',
      12: 'lg:grid-cols-12'
    };

    const gapMap = {
      'gap-2': 'gap-2',
      'gap-3': 'gap-3',
      'gap-4': 'gap-4',
      'gap-6': 'gap-6',
      'gap-8': 'gap-8'
    };

    const classes = [];

    // Default (mobile) layout
    const defaultCols = columns.default || 1;
    classes.push(defaultColsMap[defaultCols] || 'grid-cols-1');

    // Medium breakpoint (tablet)
    if (columns.md) {
      classes.push(mdColsMap[columns.md] || 'md:grid-cols-1');
    }

    // Large breakpoint (desktop)
    if (columns.lg) {
      classes.push(lgColsMap[columns.lg] || 'lg:grid-cols-1');
    }

    // Add gap
    classes.push(gapMap[gap] || gap);

    return classes.join(' ');
  };

  const renderField = (field, section, sectionIndex) => {
    const {
      id,
      name,
      type,
      label,
      multiSelect,
      columnSpan,
      validation,
      options,
      tooltip,
      labelInfo,
      labelDescription,
      className: fieldClassName,
      apiConfig,
      useSearch,
      searchField,
      dateLimit
    } = field;

    // Handle conditional visibility
    if (field.conditionalOn) {
      const watchedValue = watch(String(field.conditionalOn.field));
      if (String(watchedValue) !== String(field.conditionalOn.value)) {
        return null;
      }
    }

    // Handle nested section type
    if (type === "section") {
      return renderNestedSection(field, sectionIndex);
    }

    const renderLabel = () => {
      let labelElement = (
        <span>
          {label}
        </span>
      );

      if (tooltip) {
        labelElement = (
          <div className="flex items-center gap-2">
            <span>{label}</span>
            <div className="group relative">
              <svg className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                {tooltip}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>
          </div>
        );
      }

      if (labelInfo) {
        const badgeClass = labelInfo.style === 'optional'
          ? 'text-sm font-normal px-2 py-1 rounded-full bg-primary-100 text-primary-600'
          : 'text-sm font-normal px-2 py-1 rounded-full bg-gray-100 text-gray-600';

        labelElement = (
          <div className="flex items-center gap-2 ">
            {labelElement}
            <span className={badgeClass}>
              {labelInfo.text}
            </span>
          </div>
        );
      }

      if (labelDescription) {

        labelElement = (
          <div className="flex flex-col  gap-2">
            {labelElement}
            <span className='text-xs font-normal text-gray-600'>
              {labelDescription.text}
            </span>
          </div>
        );
      }
      return labelElement;
    };

    // Determine input styles based on field type
    const getInputStyles = () => {
      if (type === 'select') {
        const baseStyles = { ...styling.select };
        if (multiSelect) {
          return { ...baseStyles, control: { ...baseStyles.control, minHeight: '48px', height: 'auto' } };
        }
        return baseStyles;
      }
      const baseStyles = { ...styling.input };
      if (type === 'textarea') {
        return { ...baseStyles, ...styling.textarea };
      }

      if (type === 'checkbox' || type === 'checkbox-group') {
        return { ...baseStyles, ...styling.checkbox };
      }

      if (type === 'radio' || type === 'radio-group') {
        return { ...baseStyles, ...styling.radio };
      }

      return baseStyles;
    };

    // Get current field value from form - watch entire form and get specific field
    const allValues = watch();
    const fieldValue = allValues ? allValues[name] : undefined;

    const fieldProps = {
      register,
      multiSelect,
      errors,
      setValue,
      unregister,
      control,
      watch,
      type,
      label: renderLabel(),
      name,
      labelStyles: styling.label,
      styles: getInputStyles(),
      rules: validation,
      className: fieldClassName,
      apiConfig,
      useSearch,
      searchField,
      // Pass current field value for sync
      defaultValue: fieldValue,
      dateLimit,
      // File/signature upload integration
      uploadFile: (type === 'file' || type === 'signature') ? uploadFile : undefined,
      deleteFile: (type === 'file' || type === 'signature') ? deleteFile : undefined,
      isUploading: (type === 'file' || type === 'signature') ? uploadingFiles.has(getActualFieldName(field, section)) : false,
      uploadError: (type === 'file' || type === 'signature') ? uploadErrors[getActualFieldName(field, section)] : undefined,
    };

    // Add options for select fields
    if ((type === 'select' || type === "checkbox-group" || type === "radio" || type === "radio-group") && options) {
      fieldProps.options = options;
    }

    // Add column span class
    if (columnSpan) {
      fieldProps.className = fieldProps.className
        ? `${fieldProps.className} ${getColumnClasses(columnSpan)}`
        : getColumnClasses(columnSpan);
    }

    // Ensure field takes full width of its grid cell
    fieldProps.className = (fieldProps.className || '') + ' w-full';

    return (
      <InputField
        key={`${sectionIndex}-${id}`}
        {...fieldProps}
      />
    );
  };

  const renderNestedSection = (section, parentIndex) => {
    const { id, title, layout, linkedContainer, fields } = section;

    // Skip rendering LinkedFieldContainer if there are no fields
    if (!fields || fields.length === 0) {
      return (
        <div key={id} className="col-span-full mt-4 mb-2">
          {title && (
            <div className="flex items-center gap-3 mb-4">
              <h3 className="text-base font-semibold" style={{ color: metadata.brandColors.primary }}>
                {title}
              </h3>
              <div className="flex-1 h-px" style={{ backgroundColor: metadata.brandColors.border }} />
            </div>
          )}
        </div>
      );
    }

    return (
      <div key={id} className="col-span-full mt-4 mb-2">
        {/* Section title divider */}
        {title && (
          <div className="flex items-center gap-3 mb-4">
            <h3 className="text-base font-semibold" style={{ color: metadata.brandColors.primary }}>
              {title}
            </h3>
            <div className="flex-1 h-px" style={{ backgroundColor: metadata.brandColors.border }} />
          </div>
        )}
        {/* Sub-section grid with optional LinkedFieldContainer */}
        <div className={`grid grid-flow-row w-full ${getGridClasses(layout?.columns, layout?.gap)}`}>
          {linkedContainer ? (
            <>
              <LinkedFieldContainer
                displayId={linkedContainer.displayId}
                defdossierId={linkedContainer.defdossierId}
                register={register}
                errors={errors}
                setValue={setValue}
                control={control}
                layout={layout}
              >
                {fields
                  .filter((f) => f.type !== "section")
                  .map((f) => renderField(f, section, parentIndex))}
              </LinkedFieldContainer>
              {fields
                .filter((f) => f.type === "section")
                .map((f) => renderField(f, section, parentIndex))}
            </>
          ) : (
            fields.map((f) => renderField(f, section, parentIndex))
          )}
        </div>
      </div>
    );
  };

  const renderSection = (section, sectionIndex) => {
    const { id, title, description, sectionContent, layout, linkedContainer, fields, hideTitle } = section;

    const renderSectionContent = () => {
      if (!sectionContent) return null;

      const { title: contentTitle, body, sections: contentSections, footer } = sectionContent;

      return (
        <div
          className="rounded-xl p-4 sm:p-5 mb-6 text-start"
          style={{
            border: "1px solid #e6ecef",
            background: "#fbfcfd",
            color: "#1f2d3d",
            lineHeight: "1.35",
          }}
        >
          {contentTitle && (
            <h4 className="font-semibold mb-2.5" style={{ color: metadata.brandColors.primary, fontSize: "1.05em", margin: "0 0 10px 0" }}>
              {contentTitle}
            </h4>
          )}
          {body && body.map((paragraph, idx) => (
            <p key={idx} className="mb-2">
              {paragraph.includes('<') ? (
                <span dangerouslySetInnerHTML={{ __html: paragraph }} />
              ) : (
                paragraph
              )}
            </p>
          ))}
          {contentSections && (
            <ol className="list-decimal ml-5 space-y-2 mt-4">
              {contentSections.map((section, sectionIdx) => (
                <li key={sectionIdx}>
                  <strong>{section.title}</strong>
                  {section.items && (
                    <ul className="list-disc ml-4 mt-1 space-y-1">
                      {section.items.map((item, itemIdx) => (
                        <li key={itemIdx}>{item}</li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ol>
          )}
          {footer && footer.map((paragraph, idx) => (
            <p key={idx} className="mt-2 text-center">
              {paragraph.includes('<') ? (
                <span dangerouslySetInnerHTML={{ __html: paragraph }} />
              ) : (
                paragraph
              )}
            </p>
          ))}
        </div>
      );
    };

    return (
      <div key={id} className="mb-8">
        {!hideTitle && (
          <div className="flex  gap-3 mb-4">
            <div
              className="flex  justify-center w-8 h-8 rounded-full text-white text-sm font-semibold"
              style={{ backgroundColor: metadata.brandColors.primary, ...styling.index }}
            >
              {sectionIndex + 1}
            </div>
            <h2 className="text-2xl font-bold" style={{ color: metadata.brandColors.text, ...styling.title }}>
              {title}
            </h2>
          </div>
        )}

        {/* Section Description */}
        {description && (
          <div className="ml-11 mb-6">
            <p className="text-gray-600 mb-4" style={{ color: metadata.brandColors.textLight }}>
              {description}
            </p>
            <div className="h-px" style={{ backgroundColor: metadata.brandColors.border }}></div>
          </div>
        )}

        {/* Section Content (for structured content like volmacht) */}
        {renderSectionContent()}

        {/* Section Content */}
        <div className={`grid grid-flow-row w-full ${getGridClasses(layout?.columns, layout?.gap)}`}>
          {linkedContainer ? (
            <>
              <LinkedFieldContainer
                displayId={linkedContainer.displayId}
                defdossierId={linkedContainer.defdossierId}
                register={register}
                errors={errors}
                setValue={setValue}
                control={control}
                layout={layout}
                className={getColumnClasses(linkedContainer.columnSpan)}
              >
                {fields
                  .filter((field) => field.type !== "section")
                  .map((field) => renderField(field, section, sectionIndex))}
              </LinkedFieldContainer>
              {fields
                .filter((field) => field.type === "section")
                .map((field) => renderField(field, section, sectionIndex))}
            </>
          ) : (
            fields.map((field) => renderField(field, section, sectionIndex))
          )}
        </div>
      </div>
    );
  };

  const renderProgressIndicator = () => {
    if (!progress?.enabled) return null;


    return (
      <>
        {/* Spacer to prevent content jump when progress bar becomes sticky */}
        <div className="h-20"></div>

        {/* Sticky Progress Bar */}
        <div
          className="fixed top-0 left-0 right-0 z-50 px-4 py-3 transition-all duration-300 ease-in-out"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(15px)',
            borderBottom: `2px solid ${metadata.brandColors.border}`,
            boxShadow: `0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.06)`,
            background: `linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 249, 250, 0.95) 100%)`
          }}
        >
          <div className="max-w-6xl mx-auto">
            <div className="flex justify-between items-center mb-3">
              <div className="flex items-center gap-3">
                <div
                  className="flex items-center justify-center w-8 h-8 rounded-full text-white text-sm font-bold transition-all duration-300"
                  style={{
                    background: `linear-gradient(135deg, ${metadata.brandColors.primary} 0%, ${metadata.brandColors.primaryLight} 100%)`,
                    boxShadow: `0 2px 8px rgba(45, 90, 61, 0.3)`
                  }}
                >
                  {Math.round(currentProgress / 10) * 10}%
                </div>

              </div>

            </div>

            <div className="relative">
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className="h-3 rounded-full transition-all duration-500 ease-out relative"
                  style={{
                    width: `${currentProgress}%`,
                    background: `linear-gradient(90deg, ${metadata.brandColors.primary} 0%, ${metadata.brandColors.primaryLight} 50%, ${metadata.brandColors.accent} 100%)`
                  }}
                >
                  {/* Animated shine effect */}
                  <div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-pulse"
                    style={{
                      animation: currentProgress === 100 ? 'shine 2s ease-in-out' : 'none'
                    }}
                  ></div>
                </div>
              </div>

              {/* Progress milestones */}
              {[25, 50, 75, 100].map(milestone => (
                <div
                  key={milestone}
                  className="absolute top-0 w-0.5 h-3 transition-all duration-300"
                  style={{
                    left: `${milestone}%`,
                    backgroundColor: currentProgress >= milestone ? metadata.brandColors.accent : 'transparent',
                    opacity: currentProgress >= milestone ? 1 : 0.3
                  }}
                />
              ))}
            </div>
          </div>

          {/* Add custom keyframes for shine animation */}
          <style jsx>{`
            @keyframes shine {
              0% { transform: translateX(-100%); }
              100% { transform: translateX(100%); }
            }
          `}</style>
        </div>
      </>
    );
  };

  // Check if form is fully valid (all validations pass)
  const isFormFullyValid = () => {
    const watchedValues = watch();

    // Check if any files are currently uploading
    if (isAnyFileUploading) return false;

    // Check for upload errors
    if (Object.keys(uploadErrors).length > 0) return false;

    const hasErrors = Object.keys(errors).length > 0;
    if (hasErrors) return false;

    for (const section of config.sections) {
      if (section.fields) {
        for (const field of section.fields) {
          if (field.required) {
            const actualFieldName = getActualFieldName(field, section);
            const value = watchedValues[actualFieldName];
            // Handle file fields differently - check if file object exists or upload ID exists
            const isFileField = field.type === 'file' || field.type === 'signature';
            const hasValue = isFileField
              ? (value && (value instanceof File || (typeof value === 'object' && value.name))) ||
              fileUploads[actualFieldName]
              : value && value.toString().trim() !== '';

            if (!hasValue) {
              return false;
            }
          }
        }
      }
    }

    return true;
  };


  return (
    <div className={`dynamic-form-renderer w-full ${className}`}>
      {renderProgressIndicator()}

      {sections
        .sort((a, b) => a.order - b.order)
        .map((section, index) => renderSection(section, index))}

      <div className="text-center">
        <button
          type="submit"
          disabled={submission?.submitButton?.requireAllRequired && !isFormFullyValid()}
          className={`px-8 py-4 rounded-lg w-full max-w-md text-white font-semibold text-sm uppercase tracking-wide transition-all duration-200 ${(submission?.submitButton?.requireAllRequired && !isFormFullyValid())
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:scale-105 focus:outline-none focus:ring-4'
            }`}
          style={{
            ...styling.button,
            background: `linear-gradient(135deg, ${metadata.brandColors.primary} 0%, ${metadata.brandColors.primaryDark} 100%)`,
            boxShadow: `${styling.shadows.medium} rgba(45, 90, 61, 0.3)`,
            '--tw-ring-color': 'rgba(45, 90, 61, 0.2)'
          }}
          onMouseEnter={(e) => {
            if (isFormFullyValid()) {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = `0 8px 15px -3px rgba(45, 90, 61, 0.4)`;
            }
          }}
          onMouseLeave={(e) => {
            if (isFormFullyValid()) {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = `0 4px 6px -1px rgba(45, 90, 61, 0.3)`;
            }
          }}
          onClick={(e) => {
            if (!isFormFullyValid()) {
              e.preventDefault();
            }
          }}
          aria-label="Verstuur aanvraagformulier"
        >
          {isAnyFileUploading ? (
            <>
              <div className="inline-flex items-center mr-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              </div>
              Bestanden aan het uploaden...
            </>
          ) : isFormFullyValid() ? (
            submission?.submitButton?.text || 'Aanvraag Versturen'
          ) : (
            progress?.trackRequiredFields
              ? `${submission?.submitButton?.disabledText || 'Vul alle velden in'} (${currentProgress}%)`
              : (submission?.submitButton?.disabledText || 'Vul alle velden in')
          )}
        </button>

        {submission?.submitButton?.requireAllRequired && !isFormFullyValid() && (
          <p className="text-sm mt-2" style={{ color: metadata.brandColors.textLight }}>
            Los alle validatiefouten op om de aanvraag te kunnen versturen
          </p>
        )}


      </div>
    </div>
  );
};

export default DynamicFormRenderer;